import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getWatchlist, getStockList } from '../../services/stockApi';
import { StockSearch, StockList } from './components';

const StockHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'watchlist'

  // 필터 상태
  const [marketFilter, setMarketFilter] = useState('all'); // all, domestic, overseas
  const [favoriteStocks] = useState(new Set()); // 즐겨찾기 종목 (TODO: API 연동)

  // 주식 목록 조회 (비로그인 또는 목록 보기)
  const fetchStockList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getStockList({ sortBy: 'volume', limit: 20 });

      if (response.success && response.data) {
        setStocks(response.data);
      } else {
        setError(response.error || '데이터 조회 실패');
      }
    } catch (err) {
      console.error('주식 목록 조회 실패:', err);
      setError('주식 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  // 관심종목 조회 (로그인 사용자)
  const fetchWatchlist = useCallback(async () => {
    if (!user) {
      setError('로그인이 필요합니다.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await getWatchlist();

      if (response.success && response.data) {
        setStocks(response.data);
      } else {
        setError(response.error || '데이터 조회 실패');
      }
    } catch (err) {
      console.error('관심종목 조회 실패:', err);
      setError('관심종목을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // 초기 로드: 비로그인 시 주식 목록, 로그인 시 관심종목
  useEffect(() => {
    if (viewMode === 'watchlist' && user) {
      fetchWatchlist();
    } else {
      fetchStockList();
    }
  }, [viewMode, user, fetchWatchlist, fetchStockList]);

  // 종목 추가 후 새로고침
  const handleStockAdded = () => {
    if (viewMode === 'watchlist') {
      fetchWatchlist();
    }
  };

  // 종목 삭제 후 새로고침
  const handleStockDeleted = () => {
    if (viewMode === 'watchlist') {
      fetchWatchlist();
    }
    // 삭제한 종목이 선택된 종목이면 선택 해제
    if (selectedCode && !stocks.find((s) => (s.code || s.symbol) === selectedCode)) {
      setSelectedCode(null);
    }
  };

  // 새로고침
  const handleRefresh = () => {
    if (viewMode === 'watchlist' && user) {
      fetchWatchlist();
    } else {
      fetchStockList();
    }
  };

  // 종목 선택
  const handleSelectStock = (code) => {
    setSelectedCode(code);
    navigate(`/stock/${code}`);
  };

  // 시장별 필터링
  const filteredStocks = stocks.filter((stock) => {
    if (marketFilter === 'domestic') {
      return ['KOSPI', 'KOSDAQ'].includes(stock.market);
    }
    if (marketFilter === 'overseas') {
      return ['NASDAQ', 'NYSE', 'AMEX'].includes(stock.market);
    }
    return true; // all
  });

  // 시장별 종목 수
  const domesticCount = stocks.filter((s) => ['KOSPI', 'KOSDAQ'].includes(s.market)).length;
  const overseasCount = stocks.filter((s) => ['NASDAQ', 'NYSE', 'AMEX'].includes(s.market)).length;

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#0a0a0f] text-white font-[-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif]">
      {/* 헤더 */}
      <header className="flex-shrink-0 z-[100] h-14 md:h-16 bg-[rgba(10,10,15,0.8)] backdrop-blur-[12px] border-b border-[#1e1e2e]">
        <div className="max-w-[1800px] mx-auto px-4 md:px-6 h-full flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="text-2xl font-bold bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent border-none cursor-pointer transition-all duration-150 hover:opacity-80"
          >
            StockInsight
          </button>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="flex items-center justify-center p-2 bg-transparent border-none text-[#6b7280] cursor-pointer rounded-md transition-all duration-150 hover:bg-[#1e1e2e] hover:text-white lg:hidden"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <div className="flex-1 flex min-h-0 relative">
        {/* 사이드바 */}
        <aside className={`fixed left-0 z-[99] top-14 md:top-16 w-72 bg-[#12121a] border-r border-[#1e1e2e] transition-transform duration-300 flex flex-col h-[calc(100vh-56px)] md:h-[calc(100vh-64px)] lg:sticky lg:top-16 lg:!translate-x-0 ${sidebarCollapsed ? '-translate-x-full' : ''}`}>
          {/* 종목 검색 */}
          <div className="p-3 border-b border-[#1e1e2e]">
            <StockSearch onStockAdded={handleStockAdded} />
          </div>

          {/* 보기 모드 탭 */}
          <div className="flex p-2 px-3 gap-2 border-b border-[#1e1e2e]">
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 py-2.5 px-4 border rounded-lg cursor-pointer text-sm font-medium transition-all duration-150 ${viewMode === 'list' ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'bg-[#12121a] text-[#9ca3af] border-[#1e1e2e] hover:bg-[#1a1a24] hover:text-white'}`}
            >
              주요 종목
            </button>
            <button
              onClick={() => {
                if (user) {
                  setViewMode('watchlist');
                } else {
                  navigate('/login');
                }
              }}
              className={`flex-1 py-2.5 px-4 border rounded-lg cursor-pointer text-sm font-medium transition-all duration-150 ${viewMode === 'watchlist' ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'bg-[#12121a] text-[#9ca3af] border-[#1e1e2e] hover:bg-[#1a1a24] hover:text-white'}`}
            >
              관심종목 {user ? '' : '🔒'}
            </button>
          </div>

          {/* 시장 탭 */}
          <div className="flex border-b border-[#1e1e2e]">
            <button
              onClick={() => setMarketFilter('all')}
              className={`flex-1 py-2.5 px-0 bg-transparent border-none cursor-pointer text-sm font-medium transition-all duration-150 flex items-center justify-center gap-1 ${marketFilter === 'all' ? 'bg-[#3B82F6] text-white' : 'text-[#6b7280] hover:bg-[#1e1e2e] hover:text-white'}`}
            >
              전체 ({stocks.length})
            </button>
            <button
              onClick={() => setMarketFilter('domestic')}
              className={`flex-1 py-2.5 px-0 bg-transparent border-none cursor-pointer text-sm font-medium transition-all duration-150 flex items-center justify-center gap-1 ${marketFilter === 'domestic' ? 'bg-[#3B82F6] text-white' : 'text-[#6b7280] hover:bg-[#1e1e2e] hover:text-white'}`}
            >
              국내 ({domesticCount})
            </button>
            <button
              onClick={() => setMarketFilter('overseas')}
              className={`flex-1 py-2.5 px-0 bg-transparent border-none cursor-pointer text-sm font-medium transition-all duration-150 flex items-center justify-center gap-1 ${marketFilter === 'overseas' ? 'bg-[#3B82F6] text-white' : 'text-[#6b7280] hover:bg-[#1e1e2e] hover:text-white'}`}
            >
              해외 ({overseasCount})
            </button>
          </div>

          {/* 종목 리스트 */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {loading ? (
              <div className="py-12 px-6 text-center">
                <div className="w-10 h-10 mx-auto mb-4 border-[3px] border-[#1a1a24] border-t-[#3B82F6] rounded-full animate-spin"></div>
                <span className="block text-sm text-[#6b7280]">데이터 불러오는 중...</span>
              </div>
            ) : error ? (
              <div className="py-12 px-6 text-center">
                <p className="text-sm text-[#ef4444] mb-4">{error}</p>
                <button onClick={handleRefresh} className="py-2 px-4 bg-[#3B82F6] text-white border-none rounded-md text-sm font-medium cursor-pointer transition-all duration-150 hover:bg-[#2563eb]">
                  다시 시도
                </button>
              </div>
            ) : (
              <StockList
                stocks={filteredStocks}
                selectedCode={selectedCode}
                onSelect={handleSelectStock}
                onDelete={handleStockDeleted}
                favoriteStocks={favoriteStocks}
              />
            )}
          </div>
        </aside>

        {/* 오버레이 (모바일) */}
        {!sidebarCollapsed && (
          <div
            className="block fixed inset-0 top-14 md:top-16 bg-black/50 z-[98] lg:hidden"
            onClick={() => setSidebarCollapsed(true)}
          />
        )}

        {/* 메인 영역 */}
        <main className="flex-1 min-w-0 overflow-y-auto bg-[#0a0a0f] lg:ml-72">
          {!selectedCode ? (
            <div className="max-w-[600px] mx-auto py-[120px] px-6 text-center">
              <div className="w-20 h-20 mx-auto mb-6 bg-[#12121a] rounded-xl flex items-center justify-center">
                <svg className="w-10 h-10 text-[#6b7280]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">종목을 선택하세요</h2>
              <p className="text-base text-[#6b7280] leading-relaxed">왼쪽 사이드바에서 종목을 선택하거나 검색하여 추가하세요</p>
            </div>
          ) : (
            <div className="py-12 px-6 text-center">
              <p className="text-base text-[#6b7280]">종목 상세 화면이 여기에 표시됩니다</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default StockHome;
